import { useState, useEffect } from 'react';

// Keep keys in sync with deal_pipeline_router.py:LOSS_REASONS.
// Friendly labels live here; the backend stores the keys.
export const LOSS_REASON_OPTIONS = [
  { key: 'pricing',         label: 'Pricing — lease rate or sale price too high' },
  { key: 'competition',     label: 'Competition — lost to another property or broker' },
  { key: 'timing',          label: "Timing — client's timeline changed or deal stalled" },
  { key: 'property_issues', label: 'Property Issues — discovered during due diligence' },
  { key: 'financing',       label: 'Financing — funding fell through' },
  { key: 'needs_changed',   label: "Needs Changed — client's requirements shifted" },
  { key: 'location',        label: "Location — didn't work after deeper analysis" },
  { key: 'terms',           label: 'Terms — disagreement on lease terms / TI / concessions' },
  { key: 'other',           label: 'Other — explain in notes' },
];

/**
 * Loss Reason modal — opened when a deal is moved to the "lost" stage.
 * Caller passes the deal being lost, an onConfirm({reason, notes}) handler,
 * and an onCancel handler (which should roll back the optimistic stage move).
 *
 * Cancel = X button, Escape key, or clicking outside the modal.
 */
export default function LossReasonModal({
  deal,
  open,
  onConfirm,
  onCancel,
  initialReason = '',
  initialNotes = '',
  mode = 'record', // 'record' | 'edit'
}) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Seed with initial values when the modal opens for a new deal (or edit target)
  useEffect(() => {
    if (open) {
      setReason(initialReason || '');
      setNotes(initialNotes || '');
      setError('');
      setSubmitting(false);
    }
  }, [open, deal, initialReason, initialNotes]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const submit = async () => {
    if (!reason) {
      setError('Please select a reason.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({ reason, notes: notes.trim() });
    } catch (e) {
      setError(e?.message || 'Could not record loss.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={() => !submitting && onCancel()}
    >
      <div
        className="bg-[#0A0E27] rounded-xl border-l-4 border-l-[#FF6B35] border border-[#546E7A]/40 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#546E7A]/30">
          <h2 className="text-white text-lg font-semibold">
            {mode === 'edit' ? 'Edit Loss Details' : 'Why was this deal lost?'}
          </h2>
          <button
            onClick={() => !submitting && onCancel()}
            disabled={submitting}
            className="text-gray-400 hover:text-white text-2xl leading-none disabled:opacity-50"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          {deal && (
            <div className="text-sm text-gray-300 bg-[#1A2744] rounded-lg px-3 py-2 border border-[#546E7A]/20">
              <div className="text-white font-semibold">{deal.property_address}</div>
              <div className="text-gray-400 text-xs mt-0.5">{deal.buyer_name}</div>
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              Reason
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              className="w-full bg-[#1A2744] border border-[#546E7A]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6B35] disabled:opacity-50"
            >
              <option value="">— Select a reason —</option>
              {LOSS_REASON_OPTIONS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              Details
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={4}
              placeholder="What happened? Details help the AI learn from this."
              className="w-full bg-[#1A2744] border border-[#546E7A]/40 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#FF6B35] disabled:opacity-50 resize-none"
            />
          </div>

          {error && (
            <div className="text-sm text-red-300 bg-red-900/20 border border-red-500/30 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#546E7A]/30">
          <button
            onClick={() => !submitting && onCancel()}
            disabled={submitting}
            className="px-3 py-1.5 text-gray-400 hover:text-white text-sm rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !reason}
            className="px-4 py-1.5 bg-[#FF6B35] text-white text-sm font-semibold rounded-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : (mode === 'edit' ? 'Save Changes' : 'Record Loss')}
          </button>
        </div>
      </div>
    </div>
  );
}
